/**
 * ========================================
 * 모듈 5: 구글맵 커스텀 레이어 반영 모듈
 * ========================================
 *
 * 발명의 핵심: Zone 분류 결과를 구글맵 위에 시각화
 *
 * 기술적 특징:
 * 1. Google Maps API 통합
 * 2. 스팟 마커 색상 구분 (강도별)
 * 3. 지도 클릭으로 새 스팟 위치 선택
 * 4. 마커 클릭 시 정보창 표시
 *
 * 특허 청구항 매핑: 청구항 8, 9, 10
 * ========================================
 */

import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { gradeAPI } from '../services/api.service';

// 자기장 강도별 마커 색상
const MARKER_COLORS = {
  strong: '#FF0000',    // 빨강 - 강한 자기장
  medium: '#FFA500',    // 주황 - 중간 자기장
  weak: '#0066FF',      // 파랑 - 약한 자기장
  very_strong: '#FF0000', // 빨강 - 매우 강한 자기장
  extreme: '#8B0000',    // 진한 빨강 - 극강 자기장
};

// 전역 스크립트 로드 상태 관리
let isGoogleMapsLoading = false;
let googleMapsLoadPromise = null;

/**
 * Google Maps API 스크립트 로드 (싱글톤 패턴)
 */
const loadGoogleMapsScript = () => {
  // 이미 로드되어 있으면 Promise 반환
  if (window.google && window.google.maps) {
    return Promise.resolve();
  }

  // 이미 로딩 중이면 기존 Promise 반환
  if (isGoogleMapsLoading && googleMapsLoadPromise) {
    return googleMapsLoadPromise;
  }

  // 이미 스크립트 태그가 있으면 기다림
  const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
  if (existingScript) {
    googleMapsLoadPromise = new Promise((resolve) => {
      const checkInterval = setInterval(() => {
        if (window.google && window.google.maps) {
          clearInterval(checkInterval);
          resolve();
        }
      }, 100);
    });
    return googleMapsLoadPromise;
  }

  // 새로 로드 시작
  isGoogleMapsLoading = true;
  googleMapsLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    // 환경 변수 확인 (단수형/복수형 모두 지원)
    const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || 
                   import.meta.env.VITE_GOOGLE_MAP_API_KEY || 
                   'AIzaSyCnSBkbEfyVp8QEuAN27M3G-xJyy6IOGiE';
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=marker&loading=async`;
    script.async = true;
    script.defer = true;
    script.id = 'google-maps-script';

    script.onload = () => {
      isGoogleMapsLoading = false;
      resolve();
    };

    script.onerror = () => {
      isGoogleMapsLoading = false;
      console.error('Failed to load Google Maps script');
      reject(new Error('Failed to load Google Maps script'));
    };

    document.head.appendChild(script);
  });

  return googleMapsLoadPromise;
};

export default function GoogleMapContainer({ spots = [], onMapClick, selectedSpot, onMarkerClick }) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const [isMapLoaded, setIsMapLoaded] = useState(false);

  // 등급판정 기준 조회
  const { data: gradeRulesData } = useQuery({
    queryKey: ['gradeRules'],
    queryFn: gradeAPI.getGradeRules,
    staleTime: 5 * 60 * 1000, // 5분간 캐시
  });

  const gradeRules = gradeRulesData?.data?.rules || [];

  /**
   * ========================================
   * Google Maps SDK 초기화
   * ========================================
   */
  useEffect(() => {
    let isMounted = true;

    // InfoWindow 기본 스타일 오버라이드 (여백 제거 및 닫기 버튼 숨김)
    const style = document.createElement('style');
    style.id = 'google-maps-infowindow-override';
    style.textContent = `
      .gm-style .gm-style-iw-c {
        padding: 0 8px 8px 8px !important;
        padding-top: 1 !important;
        max-width: none !important;
        border-radius: 12px !important;
        margin: 0 !important;
        margin-top: 1 !important;
        top: 1 !important;
      }
      .gm-style .gm-style-iw-d {
        overflow: visible !important;
        padding: 0 !important;
        padding-top: 1 !important;
        margin: 0 !important;
        margin-top: 1 !important;
      }
      .gm-style .gm-style-iw-t {
        padding: 0 !important;
        margin: 0 !important;
      }
      .gm-style .gm-style-iw-tc {
        display: none !important;
      }
      .gm-ui-hover-effect {
        display: none !important;
      }
      .gm-style-iw.gm-style-iw-c {
        padding: 0 8px 8px 8px !important;
        padding-top: 1 !important;
        margin: 0 !important;
        margin-top: 1 !important;
      }
      .gm-style .gm-style-iw-c button {
        display: none !important;
      }
      .gm-style .gm-style-iw-c > div {
        margin-top: 1 !important;
        padding-top: 1 !important;
      }
      .gm-style .gm-style-iw-c > div > div {
        margin-top: 1 !important;
        padding-top: 1 !important;
      }
      .gm-style .gm-style-iw-c * {
        box-sizing: border-box;
      }
    `;
    if (!document.getElementById('google-maps-infowindow-override')) {
      document.head.appendChild(style);
    }

    loadGoogleMapsScript()
      .then(() => {
        // Google Maps API가 완전히 로드되었는지 확인
        const checkGoogleMapsReady = () => {
          return window.google && 
                 window.google.maps && 
                 window.google.maps.Map &&
                 window.google.maps.MapTypeId;
        };

        // API가 준비될 때까지 대기
        const waitForGoogleMaps = () => {
          if (checkGoogleMapsReady()) {
            if (isMounted) {
              initializeMap();
            }
          } else {
            // 100ms 후 다시 확인
            setTimeout(() => {
              if (isMounted) {
                waitForGoogleMaps();
              }
            }, 100);
          }
        };

        waitForGoogleMaps();
      })
      .catch((error) => {
        console.error('Google Maps initialization error:', error);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  /**
   * ========================================
   * 지도 인스턴스 초기화
   * ========================================
   */
  const initializeMap = () => {
    if (!mapRef.current || mapInstanceRef.current) return;

    // Google Maps API가 완전히 로드되었는지 확인
    if (!window.google || !window.google.maps || !window.google.maps.Map) {
      console.error('Google Maps API is not fully loaded');
      return;
    }

    const { google } = window;

    // 제주도 중심 좌표
    const jejuCenter = { lat: 33.361, lng: 126.529 };

    // 환경 변수에서 Map ID 가져오기
    const mapId = import.meta.env.VITE_GOOGLE_MAPS_MAP_ID || undefined;
    
    const mapOptions = {
      center: jejuCenter,
      zoom: 9,
      mapTypeId: google.maps.MapTypeId?.ROADMAP || 'roadmap', // 안전한 접근
      ...(mapId && { mapId: mapId }), // Map ID가 있으면 추가 (고급 마커 사용 시 필요)
      styles: [
        {
          featureType: 'poi',
          elementType: 'labels',
          stylers: [{ visibility: 'off' }],
        },
      ],
    };

    // 지도 생성
    const map = new google.maps.Map(mapRef.current, mapOptions);
    mapInstanceRef.current = map;

    // 지도 클릭 이벤트 (새 스팟 위치 선택)
    // 지도 클릭 이벤트: 모든 정보창 닫기 + 새 스팟 위치 선택
    map.addListener('click', (event) => {
      // 모든 정보창 닫기
      markersRef.current.forEach((m) => {
        if (m.infoWindow) {
          m.infoWindow.close();
        }
      });
      
      // 새 스팟 위치 선택 (onMapClick이 있는 경우)
      if (onMapClick) {
        onMapClick({
          latitude: event.latLng.lat(),
          longitude: event.latLng.lng(),
        });
      }
    });

    setIsMapLoaded(true);
  };

  /**
   * ========================================
   * 스팟 마커 렌더링
   * ========================================
   *
   * 알고리즘:
   * 1. 기존 마커 모두 제거
   * 2. spots 배열 순회
   * 3. 강도별 색상 마커 생성
   * 4. 마커 클릭 이벤트 등록
   * 5. 정보창(InfoWindow) 생성
   * ========================================
   */
  useEffect(() => {
    if (!isMapLoaded || !mapInstanceRef.current) return;

    const { google } = window;
    const map = mapInstanceRef.current;

    // 기존 마커 및 원형 제거
    markersRef.current.forEach((marker) => {
      if (marker.marker) marker.marker.setMap(null);
      if (marker.infoWindow) marker.infoWindow.close();
      if (marker.circle) marker.circle.setMap(null);
    });
    markersRef.current = [];

    // 새 마커 생성
    spots.forEach((spot) => {
      // 데이터 타입 검증
      const lat = typeof spot.latitude === 'number' ? spot.latitude : parseFloat(spot.latitude);
      const lng = typeof spot.longitude === 'number' ? spot.longitude : parseFloat(spot.longitude);

      // 유효하지 않은 좌표는 스킵
      if (isNaN(lat) || isNaN(lng)) {
        console.warn('Invalid coordinates for spot:', spot.spot_name, { lat, lng });
        return;
      }

      const position = { lat, lng };

      // 마커 색상 결정
      const markerColor = MARKER_COLORS[spot.magnetic_field_strength] || MARKER_COLORS.weak;

      // 측정등급 매핑 (스팟 목록과 동일하게 표시)
      const GRADE_MAP = {
        weak: { text: '약함', color: '#3B82F6', gradient: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 50%, #1D4ED8 100%)' },
        medium: { text: '중간', color: '#F59E0B', gradient: 'linear-gradient(135deg, #F59E0B 0%, #D97706 50%, #B45309 100%)' },
        strong: { text: '강함', color: '#EF4444', gradient: 'linear-gradient(135deg, #EF4444 0%, #DC2626 50%, #B91C1C 100%)' },
        very_strong: { text: '매우강함', color: '#DC2626', gradient: 'linear-gradient(135deg, #DC2626 0%, #B91C1C 50%, #991B1B 100%)' },
        extreme: { text: '극강', color: '#991B1B', gradient: 'linear-gradient(135deg, #991B1B 0%, #7F1D1D 50%, #450A0A 100%)' }
      };
      const gradeInfo = GRADE_MAP[spot.magnetic_field_strength] || GRADE_MAP.weak;

      // 지도 ID가 있는 경우에만 AdvancedMarkerElement 사용
      // 지도 ID가 없으면 기본 Marker 사용 (경고 방지)
      const hasMapId = map.mapId && map.mapId !== undefined;
      const canUseAdvancedMarker = hasMapId && 
        google.maps.marker && 
        google.maps.marker.AdvancedMarkerElement;
      
      if (canUseAdvancedMarker) {
        // AdvancedMarkerElement 사용 (지도 ID가 있는 경우)
        const markerElement = document.createElement('div');
        markerElement.style.width = '24px';
        markerElement.style.height = '24px';
        markerElement.style.backgroundColor = markerColor;
        markerElement.style.border = '2px solid white';
        markerElement.style.borderRadius = '50%';
        markerElement.style.boxShadow = '0 2px 6px rgba(0,0,0,0.3)';
        markerElement.style.cursor = 'pointer';
        markerElement.style.transition = 'transform 0.2s';

        const marker = new google.maps.marker.AdvancedMarkerElement({
          map: map,
          position: position,
          content: markerElement,
          title: spot.spot_name || 'Unknown',
        });

        // 마커 호버 효과
        markerElement.addEventListener('mouseenter', () => {
          markerElement.style.transform = 'scale(1.2)';
        });

        markerElement.addEventListener('mouseleave', () => {
          markerElement.style.transform = 'scale(1)';
        });

        // 정보창 생성 (모던하고 세련된 디자인)
        const strengthColor = gradeInfo.color;
        const strengthGradient = gradeInfo.gradient;
        const strengthText = gradeInfo.text;
        
        const infoWindowContent = `
          <div style="
            width: 300px;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Noto Sans KR', sans-serif;
            overflow: hidden;
            border-radius: 12px;
            box-shadow: 0 12px 40px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.05);
            background: white;
            margin: 0;
            padding: 0;
            display: flex;
            flex-direction: column;
            box-sizing: border-box;
          ">
            <!-- Compact Header -->
            <div style="
              background: ${strengthGradient};
              padding: 10px 12px;
              color: white;
              position: relative;
              overflow: hidden;
              margin: 0;
              border-radius: 12px 12px 0 0;
            ">
              <div style="
                position: absolute;
                top: -50%;
                right: -20%;
                width: 120px;
                height: 120px;
                background: radial-gradient(circle, rgba(255,255,255,0.2) 0%, transparent 70%);
                border-radius: 50%;
              "></div>
              <div style="
                display: flex;
                align-items: center;
                justify-content: space-between;
                gap: 8px;
                position: relative;
                z-index: 1;
              ">
                <h3 style="
                  margin: 0;
                  font-size: 17px;
                  font-weight: 900;
                  color: white;
                  line-height: 1.3;
                  flex: 1;
                  overflow: hidden;
                  text-overflow: ellipsis;
                  white-space: nowrap;
                  text-shadow: 0 2px 4px rgba(0,0,0,0.2);
                ">
                  ${spot.spot_name || 'Unknown'}
                </h3>
                <div style="
                  display: inline-flex;
                  align-items: center;
                  gap: 5px;
                  padding: 4px 10px;
                  background: rgba(255, 255, 255, 0.3);
                  backdrop-filter: blur(12px);
                  -webkit-backdrop-filter: blur(12px);
                  border-radius: 10px;
                  font-size: 10px;
                  font-weight: 800;
                  flex-shrink: 0;
                  border: 1px solid rgba(255, 255, 255, 0.4);
                  box-shadow: 0 2px 6px rgba(0,0,0,0.15);
                ">
                  <span style="
                    width: 6px;
                    height: 6px;
                    background: white;
                    border-radius: 50%;
                    display: inline-block;
                    box-shadow: 0 0 4px rgba(255,255,255,0.9);
                  "></span>
                  ${strengthText}
                </div>
              </div>
            </div>
            
            <!-- Compact Content -->
            <div style="
              padding: 12px;
              background: white;
              margin: 0;
            ">
              ${spot.nanotesla_value ? `
                <div style="
                  display: flex;
                  align-items: center;
                  gap: 10px;
                  padding: 10px;
                  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
                  border-radius: 8px;
                  margin-bottom: 10px;
                  border: 1px solid rgba(99, 102, 241, 0.1);
                  box-shadow: 0 1px 4px rgba(99, 102, 241, 0.08);
                  position: relative;
                  overflow: hidden;
                ">
                  <div style="
                    position: absolute;
                    top: 0;
                    left: 0;
                    width: 3px;
                    height: 100%;
                    background: ${strengthColor};
                  "></div>
                  <div style="
                    width: 32px;
                    height: 32px;
                    background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
                    border-radius: 6px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: white;
                    font-weight: 800;
                    font-size: 10px;
                    flex-shrink: 0;
                    box-shadow: 0 2px 8px rgba(99, 102, 241, 0.3);
                    margin-left: 3px;
                  ">
                    µT
                  </div>
                  <div style="flex: 1; min-width: 0;">
                    <div style="font-size: 10px; color: #475569; margin-bottom: 5px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px;">측정값</div>
                    <div style="font-size: 18px; font-weight: 900; color: #0f172a; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; letter-spacing: 0px;">
                      ${spot.nanotesla_value ? (parseFloat(spot.nanotesla_value) / 1000).toFixed(2) : '0.00'} µT
                    </div>
                  </div>
                </div>
              ` : ''}
              
              ${spot.detailed_benefits ? `
                <div style="
                  padding: 10px;
                  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 30%, #fef9c3 100%);
                  border-radius: 8px;
                  margin-bottom: 10px;
                  border: 1px solid rgba(245, 158, 11, 0.2);
                  box-shadow: 0 1px 4px rgba(245, 158, 11, 0.1);
                ">
                  <div style="
                    font-size: 10px;
                    color: #78350f;
                    margin-bottom: 7px;
                    font-weight: 800;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    display: flex;
                    align-items: center;
                    gap: 5px;
                  ">
                    <span style="font-size: 12px;">✨</span>
                    <span>상세 효능</span>
                  </div>
                  <div style="
                    font-size: 13px;
                    color: #92400e;
                    line-height: 1.7;
                    max-height: 80px;
                    overflow-y: auto;
                    word-break: keep-all;
                    font-weight: 600;
                  ">
                    ${spot.detailed_benefits}
                  </div>
                </div>
              ` : ''}
              
              <div style="
                display: grid;
                grid-template-columns: 1fr 1fr;
                gap: 8px;
                margin-bottom: 10px;
              ">
                <div style="
                  padding: 8px;
                  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
                  border-radius: 6px;
                  text-align: center;
                  border: 1px solid rgba(0,0,0,0.05);
                  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
                ">
                  <div style="font-size: 9px; color: #475569; margin-bottom: 5px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.4px;">반경</div>
                  <div style="font-size: 14px; font-weight: 800; color: #0f172a; letter-spacing: 0px;">
                    ${spot.geofence_radius_meters || 50}m
                  </div>
                </div>
                <div style="
                  padding: 8px;
                  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
                  border-radius: 6px;
                  text-align: center;
                  border: 1px solid rgba(0,0,0,0.05);
                  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
                ">
                  <div style="font-size: 9px; color: #475569; margin-bottom: 5px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.4px;">좌표</div>
                  <div style="font-size: 11px; font-weight: 700; color: #0f172a; font-family: 'SF Mono', 'Monaco', 'Consolas', monospace; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
                    ${parseFloat(spot.latitude).toFixed(3)}, ${parseFloat(spot.longitude).toFixed(3)}
                  </div>
                </div>
              </div>
              
              ${spot.address ? `
                <div style="
                  padding: 8px 10px;
                  background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
                  border-radius: 6px;
                  font-size: 11px;
                  color: #334155;
                  border-left: 2px solid #94a3b8;
                  overflow: hidden;
                  text-overflow: ellipsis;
                  white-space: nowrap;
                  font-weight: 600;
                  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
                ">
                  📍 ${spot.address}
                </div>
              ` : ''}
            </div>
          </div>
        `;

        const infoWindow = new google.maps.InfoWindow({
          content: infoWindowContent,
          disableAutoPan: false,
        });
        
        // InfoWindow가 열릴 때 상단 여백만 제거하고 좌우/하단 여백 유지
        google.maps.event.addListener(infoWindow, 'domready', () => {
          const iwContainer = document.querySelector('.gm-style-iw-c');
          if (iwContainer) {
            iwContainer.style.padding = '0 8px 8px 8px';
            iwContainer.style.paddingTop = '1';
            iwContainer.style.margin = '0';
            iwContainer.style.marginTop = '1';
            iwContainer.style.top = '1';
            const iwContent = iwContainer.querySelector('.gm-style-iw-d');
            if (iwContent) {
              iwContent.style.padding = '0';
              iwContent.style.paddingTop = '1';
              iwContent.style.margin = '0';
              iwContent.style.marginTop = '1';
            }
            // 모든 자식 요소의 상단 여백 제거
            const allChildren = iwContainer.querySelectorAll('*');
            allChildren.forEach((child) => {
              if (child.style) {
                child.style.marginTop = '1';
                child.style.paddingTop = '1';
              }
            });
          }
        });

        // 마커 클릭 이벤트 (토글 기능)
        marker.addListener('click', () => {
          // 현재 정보창이 열려있는지 확인
          const currentInfoWindow = markersRef.current.find(m => 
            m.marker === marker && m.infoWindow
          );
          
          // 이미 열려있으면 닫기
          if (currentInfoWindow && currentInfoWindow.infoWindow) {
            const isOpen = currentInfoWindow.infoWindow.getMap() !== null;
            if (isOpen) {
              currentInfoWindow.infoWindow.close();
              return;
            }
          }
          
          // 모든 정보창 닫기
          markersRef.current.forEach((m) => {
            if (m.infoWindow) {
              m.infoWindow.close();
            }
          });

          // 현재 정보창 열기
          infoWindow.open({
            anchor: marker,
            map: map,
          });
          
          // InfoWindow가 열린 후 스타일 강제 적용
          setTimeout(() => {
            const iwContainer = document.querySelector('.gm-style-iw-c');
            if (iwContainer) {
              iwContainer.style.padding = '0';
              iwContainer.style.margin = '0';
              const iwContent = iwContainer.querySelector('.gm-style-iw-d');
              if (iwContent) {
                iwContent.style.padding = '0';
                iwContent.style.margin = '0';
              }
            }
          }, 100);

          // 부모 컴포넌트에 알림
          if (onMarkerClick) {
            onMarkerClick(spot);
          }
        });

        // 반경 원형 표시 (반투명 레이더 스타일)
        const radiusMeters = spot.geofence_radius_meters || 50;
        const circle = new google.maps.Circle({
          strokeColor: markerColor,
          strokeOpacity: 0.6,
          strokeWeight: 2,
          fillColor: markerColor,
          fillOpacity: 0.15,
          map: map,
          center: position,
          radius: radiusMeters,
          zIndex: 1, // 마커 아래에 표시
        });

        markersRef.current.push({
          marker: marker,
          infoWindow: infoWindow,
          spot: spot,
          circle: circle,
        });
      } else {
        // 기본 Marker 사용 (지도 ID 없이도 안정적으로 작동)
        const markerIcon = {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 12,
          fillColor: markerColor,
          fillOpacity: 1,
          strokeColor: '#FFFFFF',
          strokeWeight: 2,
        };

        const marker = new google.maps.Marker({
          position: position,
          map: map,
          icon: markerIcon,
          title: spot.spot_name || 'Unknown',
        });

        // 정보창 생성 (모던하고 세련된 디자인) - gradeInfo 재사용
        const strengthColor = gradeInfo.color;
        const strengthGradient = gradeInfo.gradient;
        const strengthText = gradeInfo.text;
        
        const infoWindowContent = `
          <div style="
            width: 300px;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Noto Sans KR', sans-serif;
            overflow: hidden;
            border-radius: 12px;
            box-shadow: 0 12px 40px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.05);
            background: white;
            margin: 0;
            padding: 0;
            display: flex;
            flex-direction: column;
            box-sizing: border-box;
          ">
            <!-- Compact Header -->
            <div style="
              background: ${strengthGradient};
              padding: 10px 12px;
              color: white;
              position: relative;
              overflow: hidden;
              margin: 0;
              border-radius: 12px 12px 0 0;
            ">
              <div style="
                position: absolute;
                top: -50%;
                right: -20%;
                width: 120px;
                height: 120px;
                background: radial-gradient(circle, rgba(255,255,255,0.2) 0%, transparent 70%);
                border-radius: 50%;
              "></div>
              <div style="
                display: flex;
                align-items: center;
                justify-content: space-between;
                gap: 8px;
                position: relative;
                z-index: 1;
              ">
                <h3 style="
                  margin: 0;
                  font-size: 17px;
                  font-weight: 900;
                  color: white;
                  line-height: 1.3;
                  flex: 1;
                  overflow: hidden;
                  text-overflow: ellipsis;
                  white-space: nowrap;
                  text-shadow: 0 2px 4px rgba(0,0,0,0.2);
                ">
                  ${spot.spot_name || 'Unknown'}
                </h3>
                <div style="
                  display: inline-flex;
                  align-items: center;
                  gap: 5px;
                  padding: 4px 10px;
                  background: rgba(255, 255, 255, 0.3);
                  backdrop-filter: blur(12px);
                  -webkit-backdrop-filter: blur(12px);
                  border-radius: 10px;
                  font-size: 10px;
                  font-weight: 800;
                  flex-shrink: 0;
                  border: 1px solid rgba(255, 255, 255, 0.4);
                  box-shadow: 0 2px 6px rgba(0,0,0,0.15);
                ">
                  <span style="
                    width: 6px;
                    height: 6px;
                    background: white;
                    border-radius: 50%;
                    display: inline-block;
                    box-shadow: 0 0 4px rgba(255,255,255,0.9);
                  "></span>
                  ${strengthText}
                </div>
              </div>
            </div>
            
            <!-- Compact Content -->
            <div style="
              padding: 12px;
              background: white;
              margin: 0;
            ">
              ${spot.nanotesla_value ? `
                <div style="
                  display: flex;
                  align-items: center;
                  gap: 10px;
                  padding: 10px;
                  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
                  border-radius: 8px;
                  margin-bottom: 10px;
                  border: 1px solid rgba(99, 102, 241, 0.1);
                  box-shadow: 0 1px 4px rgba(99, 102, 241, 0.08);
                  position: relative;
                  overflow: hidden;
                ">
                  <div style="
                    position: absolute;
                    top: 0;
                    left: 0;
                    width: 3px;
                    height: 100%;
                    background: ${strengthColor};
                  "></div>
                  <div style="
                    width: 32px;
                    height: 32px;
                    background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
                    border-radius: 6px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: white;
                    font-weight: 800;
                    font-size: 10px;
                    flex-shrink: 0;
                    box-shadow: 0 2px 8px rgba(99, 102, 241, 0.3);
                    margin-left: 3px;
                  ">
                    µT
                  </div>
                  <div style="flex: 1; min-width: 0;">
                    <div style="font-size: 10px; color: #475569; margin-bottom: 5px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px;">측정값</div>
                    <div style="font-size: 18px; font-weight: 900; color: #0f172a; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; letter-spacing: 0px;">
                      ${spot.nanotesla_value ? (parseFloat(spot.nanotesla_value) / 1000).toFixed(2) : '0.00'} µT
                    </div>
                  </div>
                </div>
              ` : ''}
              
              ${spot.detailed_benefits ? `
                <div style="
                  padding: 10px;
                  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 30%, #fef9c3 100%);
                  border-radius: 8px;
                  margin-bottom: 10px;
                  border: 1px solid rgba(245, 158, 11, 0.2);
                  box-shadow: 0 1px 4px rgba(245, 158, 11, 0.1);
                ">
                  <div style="
                    font-size: 10px;
                    color: #78350f;
                    margin-bottom: 7px;
                    font-weight: 800;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    display: flex;
                    align-items: center;
                    gap: 5px;
                  ">
                    <span style="font-size: 12px;">✨</span>
                    <span>상세 효능</span>
                  </div>
                  <div style="
                    font-size: 13px;
                    color: #92400e;
                    line-height: 1.7;
                    max-height: 80px;
                    overflow-y: auto;
                    word-break: keep-all;
                    font-weight: 600;
                  ">
                    ${spot.detailed_benefits}
                  </div>
                </div>
              ` : ''}
              
              <div style="
                display: grid;
                grid-template-columns: 1fr 1fr;
                gap: 8px;
                margin-bottom: 10px;
              ">
                <div style="
                  padding: 8px;
                  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
                  border-radius: 6px;
                  text-align: center;
                  border: 1px solid rgba(0,0,0,0.05);
                  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
                ">
                  <div style="font-size: 9px; color: #475569; margin-bottom: 5px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.4px;">반경</div>
                  <div style="font-size: 14px; font-weight: 800; color: #0f172a; letter-spacing: 0px;">
                    ${spot.geofence_radius_meters || 50}m
                  </div>
                </div>
                <div style="
                  padding: 8px;
                  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
                  border-radius: 6px;
                  text-align: center;
                  border: 1px solid rgba(0,0,0,0.05);
                  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
                ">
                  <div style="font-size: 9px; color: #475569; margin-bottom: 5px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.4px;">좌표</div>
                  <div style="font-size: 11px; font-weight: 700; color: #0f172a; font-family: 'SF Mono', 'Monaco', 'Consolas', monospace; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
                    ${parseFloat(spot.latitude).toFixed(3)}, ${parseFloat(spot.longitude).toFixed(3)}
                  </div>
                </div>
              </div>
              
              ${spot.address ? `
                <div style="
                  padding: 8px 10px;
                  background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
                  border-radius: 6px;
                  font-size: 11px;
                  color: #334155;
                  border-left: 2px solid #94a3b8;
                  overflow: hidden;
                  text-overflow: ellipsis;
                  white-space: nowrap;
                  font-weight: 600;
                  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
                ">
                  📍 ${spot.address}
                </div>
              ` : ''}
            </div>
          </div>
        `;

        const infoWindow = new google.maps.InfoWindow({
          content: infoWindowContent,
          disableAutoPan: false,
        });
        
        // InfoWindow가 열릴 때 상단 여백만 제거하고 좌우/하단 여백 유지
        google.maps.event.addListener(infoWindow, 'domready', () => {
          const iwContainer = document.querySelector('.gm-style-iw-c');
          if (iwContainer) {
            iwContainer.style.padding = '0 8px 8px 8px';
            iwContainer.style.paddingTop = '0';
            iwContainer.style.margin = '0';
            iwContainer.style.marginTop = '0';
            iwContainer.style.top = '0';
            const iwContent = iwContainer.querySelector('.gm-style-iw-d');
            if (iwContent) {
              iwContent.style.padding = '0';
              iwContent.style.paddingTop = '0';
              iwContent.style.margin = '0';
              iwContent.style.marginTop = '0';
            }
            // 모든 자식 요소의 상단 여백 제거
            const allChildren = iwContainer.querySelectorAll('*');
            allChildren.forEach((child) => {
              if (child.style) {
                child.style.marginTop = '0';
                child.style.paddingTop = '0';
              }
            });
          }
        });

        // 마커 클릭 이벤트 (토글 기능)
        marker.addListener('click', () => {
          // 현재 정보창이 열려있는지 확인
          const currentInfoWindow = markersRef.current.find(m => 
            m.marker === marker && m.infoWindow
          );
          
          // 이미 열려있으면 닫기
          if (currentInfoWindow && currentInfoWindow.infoWindow) {
            const isOpen = currentInfoWindow.infoWindow.getMap() !== null;
            if (isOpen) {
              currentInfoWindow.infoWindow.close();
              return;
            }
          }
          
          // 모든 정보창 닫기
          markersRef.current.forEach((m) => {
            if (m.infoWindow) {
              m.infoWindow.close();
            }
          });

          // 현재 정보창 열기
          infoWindow.open(map, marker);

          // 부모 컴포넌트에 알림
          if (onMarkerClick) {
            onMarkerClick(spot);
          }
        });

        // 마커 호버 효과
        marker.addListener('mouseover', () => {
          marker.setIcon({
            ...markerIcon,
            scale: 14,
          });
        });

        marker.addListener('mouseout', () => {
          marker.setIcon(markerIcon);
        });

        // 반경 원형 표시 (반투명 레이더 스타일)
        const radiusMeters = spot.geofence_radius_meters || 50;
        const circle = new google.maps.Circle({
          strokeColor: markerColor,
          strokeOpacity: 0.6,
          strokeWeight: 2,
          fillColor: markerColor,
          fillOpacity: 0.15,
          map: map,
          center: position,
          radius: radiusMeters,
          zIndex: 1, // 마커 아래에 표시
        });

        markersRef.current.push({
          marker: marker,
          infoWindow: infoWindow,
          spot: spot,
          circle: circle,
        });
      }
    });

    // 선택된 스팟이 있으면 지도 중심 이동하고 팝업 열기
    if (selectedSpot && selectedSpot.latitude != null && selectedSpot.longitude != null) {
      const selectedLat = typeof selectedSpot.latitude === 'number' 
        ? selectedSpot.latitude 
        : parseFloat(selectedSpot.latitude);
      const selectedLng = typeof selectedSpot.longitude === 'number' 
        ? selectedSpot.longitude 
        : parseFloat(selectedSpot.longitude);

      if (!isNaN(selectedLat) && !isNaN(selectedLng)) {
        const selectedPosition = {
          lat: selectedLat,
          lng: selectedLng,
        };
        map.setCenter(selectedPosition);
        map.setZoom(15); // 확대

        // 해당 마커의 InfoWindow 열기
        const markerData = markersRef.current.find(m => m.spot?.uuid === selectedSpot.uuid);
        if (markerData && markerData.infoWindow) {
          // 모든 다른 InfoWindow 닫기
          markersRef.current.forEach((m) => {
            if (m.infoWindow && m !== markerData) {
              m.infoWindow.close();
            }
          });

          // 해당 마커의 InfoWindow 열기
          if (markerData.marker) {
            markerData.infoWindow.open({
              anchor: markerData.marker,
              map: map,
            });
            
            // InfoWindow가 열린 후 스타일 강제 적용
            setTimeout(() => {
              const iwContainer = document.querySelector('.gm-style-iw-c');
              if (iwContainer) {
                iwContainer.style.padding = '0 8px 8px 8px';
                iwContainer.style.paddingTop = '1';
                iwContainer.style.margin = '0';
                iwContainer.style.marginTop = '1';
                iwContainer.style.top = '1';
                const iwContent = iwContainer.querySelector('.gm-style-iw-d');
                if (iwContent) {
                  iwContent.style.padding = '0';
                  iwContent.style.paddingTop = '1';
                  iwContent.style.margin = '0';
                  iwContent.style.marginTop = '1';
                }
              }
            }, 100);
          }
        }
      }
    }
  }, [spots, isMapLoaded, selectedSpot, onMarkerClick]);

  return (
    <div className="relative">
      {/* Map Container */}
      <div
        ref={mapRef}
        className="w-full h-[600px] rounded-lg shadow-lg"
        style={{ backgroundColor: '#f0f0f0' }}
      />

      {/* Map Loading Indicator */}
      {!isMapLoaded && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 rounded-lg">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
            <p className="text-gray-600">지도 로딩 중...</p>
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="absolute top-4 right-4 bg-white rounded-lg shadow-lg p-4 z-10 min-w-[200px]">
        <h4 className="text-sm font-semibold text-gray-900 mb-3">자기장 강도</h4>
        <div className="space-y-2">
          {gradeRules.length > 0 ? (
            // 등급판정 기준 값으로 표시
            gradeRules
              .sort((a, b) => parseFloat(a.min_ut_threshold) - parseFloat(b.min_ut_threshold))
              .map((rule) => {
                // 등급에 따른 색상 매핑
                let markerColor = MARKER_COLORS.weak;
                if (rule.grade === 'extreme') {
                  markerColor = MARKER_COLORS.extreme || '#8B0000';
                } else if (rule.grade === 'very_strong') {
                  markerColor = MARKER_COLORS.very_strong || MARKER_COLORS.strong;
                } else if (rule.grade === 'strong') {
                  markerColor = MARKER_COLORS.strong;
                } else if (rule.grade === 'medium') {
                  markerColor = MARKER_COLORS.medium;
                } else {
                  markerColor = MARKER_COLORS.weak;
                }

                // 범위 표시 형식
                const maxDisplay = rule.max_ut_threshold >= 999999 
                  ? '120+' 
                  : parseFloat(rule.max_ut_threshold).toFixed(0);
                const rangeText = `${parseFloat(rule.min_ut_threshold).toFixed(0)}-${maxDisplay} µT`;

                return (
                  <div key={rule.grade} className="flex items-center space-x-2">
                    <div
                      className="w-4 h-4 rounded-full border-2 border-white shadow flex-shrink-0"
                      style={{ backgroundColor: markerColor }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-semibold text-gray-900">{rule.grade_display}</div>
                      <div className="text-xs text-gray-600">{rangeText}</div>
                    </div>
                  </div>
                );
              })
          ) : (
            // 기본 범례 (등급판정 기준이 없을 경우)
            <>
              <div className="flex items-center space-x-2">
                <div
                  className="w-4 h-4 rounded-full border-2 border-white shadow"
                  style={{ backgroundColor: MARKER_COLORS.strong }}
                />
                <span className="text-xs text-gray-700">강함 (Strong)</span>
              </div>
              <div className="flex items-center space-x-2">
                <div
                  className="w-4 h-4 rounded-full border-2 border-white shadow"
                  style={{ backgroundColor: MARKER_COLORS.medium }}
                />
                <span className="text-xs text-gray-700">중간 (Medium)</span>
              </div>
              <div className="flex items-center space-x-2">
                <div
                  className="w-4 h-4 rounded-full border-2 border-white shadow"
                  style={{ backgroundColor: MARKER_COLORS.weak }}
                />
                <span className="text-xs text-gray-700">약함 (Weak)</span>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Click Instruction */}
      {onMapClick && (
        <div className="absolute bottom-4 left-4 bg-blue-600 text-white rounded-lg shadow-lg px-4 py-2 z-10">
          <p className="text-sm">
            💡 지도를 클릭하여 새 스팟 위치를 선택하세요
          </p>
        </div>
      )}
    </div>
  );
}
